import { createClient } from '@supabase/supabase-js';

// ---------------------------------------------------------------------------
// Runtime Supabase configuration (Phase 81.5.4 post-deploy hardening).
//
// Historically the Supabase URL and anon key were sourced from
// `process.env.REACT_APP_SUPABASE_URL` / `..._SUPABASE_ANON_KEY`. Create React
// App inlines those into the JS bundle **at build time**, which means every
// deployment carries the config baked in. When Production was deployed from
// the Preview source tree, the Preview Supabase URL (`ousijeaolxcfwhwiurlj`)
// was baked into the Prod bundle even though Prod's runtime environment had
// the correct new-Prod-DB URL (`vmocboeatomlmbdrmnto`). Users hit "Invalid
// login credentials" because the browser talked to the wrong Supabase project.
//
// Fix: fetch `/api/public-config` from the backend (whose runtime env vars
// ARE environment-scoped by Emergent's deploy) BEFORE constructing the
// Supabase client. `bootstrapSupabaseConfig()` runs once in `index.js`
// before React renders. If the fetch fails (e.g. offline dev, backend
// unreachable), we fall back to the baked-in `REACT_APP_*` values so nothing
// regresses. The client is lazy: import sites keep writing
// `import { supabase } from '.../supabaseClient'` and never see the change.
// ---------------------------------------------------------------------------

const BAKED_URL = process.env.REACT_APP_SUPABASE_URL;
const BAKED_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;

let _resolvedUrl = null;
let _resolvedKey = null;
let _client = null;

// v3.6.9 Phase 84.28.7 — bootstrap status is exported so the login page
// can differentiate between "runtime config successfully loaded" (safe to
// submit credentials) and "we fell back to the baked-in build values"
// (potentially wrong project — show a Connecting… state and offer retry).
let _bootstrapStatus = {
  resolved: false,   // has ANY value been set yet?
  source: null,      // 'runtime' | 'baked' | null
  matches_baked: null, // when runtime resolved: does its URL equal the baked URL?
  attempts: 0,       // how many fetch attempts we made (max 2)
  last_error: null,  // last fetch error message, if any
};

export function getBootstrapStatus() {
  // Return a shallow copy so callers can't mutate internal state.
  return { ..._bootstrapStatus };
}

/**
 * Fetch `/api/public-config` and store the runtime Supabase URL + anon key.
 *
 * v3.6.9 Phase 84.28.7 — hardened for Emergent cold-start:
 *   • Per-attempt timeout is 4 seconds (previously the caller had one
 *     3-second hard cap for the whole thing — too tight for a cold pod).
 *   • ONE automatic retry with a 500ms delay after the first timeout.
 *     Cold-start failures are almost always transient; one retry usually
 *     eliminates them without perceptibly delaying the happy path.
 *   • If both attempts fail, we STILL fall back to the baked-in build
 *     values so the app never renders a blank page — but we tag the
 *     bootstrap `source='baked'` so the login page can show a warm
 *     "Connecting securely…" state with a Retry button instead of
 *     silently submitting credentials to the wrong Supabase project.
 *   • Idempotent: subsequent calls after a successful resolve are no-ops.
 *     Callers wanting to force a fresh probe (e.g. Retry button on Login)
 *     should call `retryBootstrap()` instead.
 */
async function _fetchOnce(backend, ms) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await fetch(`${backend}/api/public-config`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      signal: ctrl.signal,
    });
    if (!res.ok) throw new Error(`public-config HTTP ${res.status}`);
    const cfg = await res.json();
    if (!cfg?.supabase_url || !cfg?.supabase_anon_key) {
      throw new Error('public-config missing supabase_url / supabase_anon_key');
    }
    return cfg;
  } finally {
    clearTimeout(t);
  }
}

export async function bootstrapSupabaseConfig() {
  if (_resolvedUrl && _resolvedKey && _bootstrapStatus.source === 'runtime') {
    return; // already bootstrapped from runtime, don't re-fetch
  }
  const backend = process.env.REACT_APP_BACKEND_URL;
  const PER_ATTEMPT_MS = 4000;
  if (backend) {
    for (let attempt = 1; attempt <= 2; attempt++) {
      _bootstrapStatus.attempts = attempt;
      try {
        const cfg = await _fetchOnce(backend, PER_ATTEMPT_MS);
        _resolvedUrl = cfg.supabase_url;
        _resolvedKey = cfg.supabase_anon_key;
        _bootstrapStatus.resolved = true;
        _bootstrapStatus.source = 'runtime';
        _bootstrapStatus.matches_baked = (BAKED_URL || '') === cfg.supabase_url;
        _bootstrapStatus.last_error = null;
        return;
      } catch (e) {
        _bootstrapStatus.last_error = (e && e.message) ? e.message : String(e);
        if (attempt === 1) {
          // Small breather before retry — cold pods usually warm up in <1s.
          await new Promise((r) => setTimeout(r, 500));
          continue;
        }
        if (typeof console !== 'undefined') {
          // eslint-disable-next-line no-console
          console.warn('[2120] /api/public-config failed twice; falling back to baked-in:', _bootstrapStatus.last_error);
        }
      }
    }
  }
  // Fallback — baked-in values remain the safety net for local dev / offline.
  _resolvedUrl = BAKED_URL;
  _resolvedKey = BAKED_ANON;
  _bootstrapStatus.resolved = true;
  _bootstrapStatus.source = 'baked';
  _bootstrapStatus.matches_baked = true;
}

/**
 * Manual retry entry-point wired to the "Retry" button on Login when a
 * cold-start fallback happened. Resets the resolved values and re-runs
 * the fetch loop. Also invalidates the constructed client so its next
 * access re-materialises against the (hopefully) freshly-resolved URL.
 */
export async function retryBootstrap() {
  _resolvedUrl = null;
  _resolvedKey = null;
  _client = null;
  _bootstrapStatus = {
    resolved: false,
    source: null,
    matches_baked: null,
    attempts: 0,
    last_error: null,
  };
  await bootstrapSupabaseConfig();
  return getBootstrapStatus();
}

// ---------------------------------------------------------------------------
// Storage probe, Safari Private Browsing throws QuotaExceededError on every
// setItem call. When that's the case we let the SDK fall back to its in-memory
// store rather than passing a wrapper (wrappers have caused regressions with
// newer supabase-js versions in the past).
// ---------------------------------------------------------------------------
function storageIsWritable() {
  try {
    const probe = '__2120_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

const canPersist = typeof window !== 'undefined' && storageIsWritable();

// ---------------------------------------------------------------------------
// Pristine fetch (Phase 38.6, preview-env fix).
//
// The Emergent preview environment injects third-party telemetry scripts
// (PostHog session recording + cloudflareinsights beacon + emergent-main.js)
// that monkey-patch `window.fetch` to capture response bodies for analytics.
// At least one of them consumes the response body via `.text()` / `.json()`
// without first calling `.clone()`. When supabase-js then calls `.json()` on
// that same Response, it throws:
//
//   • Chromium:  "Failed to execute 'json' on 'Response': body stream already read"
//   • Safari:    "Body is disturbed or locked"
//
// Production (app.2120.money) does not load any of these scripts, so the bug
// is preview-only, but we still need to be robust against ANY future
// fetch-wrapping by extensions, monitoring vendors, or platform integrations.
//
// The fix: grab a pristine `fetch` reference from a hidden same-origin
// iframe. Third-party scripts patch the parent window's `fetch`; the iframe's
// `contentWindow.fetch` remains untouched. Pass that to supabase-js as
// `global.fetch` so auth requests bypass the wrappers entirely.
//
// The iframe is kept alive for the lifetime of the page so the bound fetch
// reference stays valid. If the iframe approach fails for any reason
// (sandbox, CSP, exotic browser), we silently fall back to the wrapped
// `window.fetch`, at worst, behavior is identical to today.
// ---------------------------------------------------------------------------
function makePristineFetch() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return undefined; // SSR, caller should never get here, but be defensive.
  }
  // Fast path: if fetch is still native, nothing to defend against.
  try {
    if (/\[native code\]/.test(window.fetch?.toString() || '')) {
      return window.fetch.bind(window);
    }
  } catch {
    /* ignore */
  }
  try {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('tabindex', '-1');
    iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden;pointer-events:none;';
    document.documentElement.appendChild(iframe);
    const inner = iframe.contentWindow;
    if (!inner || typeof inner.fetch !== 'function') {
      iframe.remove();
      return window.fetch.bind(window);
    }
    // Keep the iframe alive, the bound fetch references the iframe's
    // contentWindow. Removing it would break subsequent calls.
    const bound = inner.fetch.bind(inner);
    // Mark for easy debugging in DevTools.
    try { iframe.dataset.purpose = '2120-pristine-fetch'; } catch { /* ignore */ }
    return bound;
  } catch {
    return window.fetch ? window.fetch.bind(window) : undefined;
  }
}

const pristineFetch = makePristineFetch();

/**
 * Lazily construct the Supabase client on first access. If
 * `bootstrapSupabaseConfig()` was awaited during app startup, we use those
 * values; otherwise we fall back to the baked-in `REACT_APP_*` values so
 * imports never blow up on synchronous access (e.g. during tests).
 */
function getClient() {
  if (_client) return _client;
  const url = _resolvedUrl || BAKED_URL;
  const key = _resolvedKey || BAKED_ANON;
  _client = createClient(url, key, {
    auth: {
      persistSession: canPersist,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: canPersist ? window.localStorage : undefined,
    },
    // `global.fetch` is supported by supabase-js v2, used for every HTTP call
    // (auth, REST, storage, functions). Passing our iframe-sourced fetch keeps
    // every Supabase response body untouched by parent-window fetch wrappers.
    global: pristineFetch ? { fetch: pristineFetch } : undefined,
  });
  return _client;
}

// Proxy so every existing `import { supabase } from '.../supabaseClient'`
// keeps working unchanged — property access lazily materializes the client.
export const supabase = new Proxy(
  {},
  {
    get(_target, prop) {
      const c = getClient();
      const val = c[prop];
      return typeof val === 'function' ? val.bind(c) : val;
    },
    has(_target, prop) {
      return prop in getClient();
    },
  },
);

// Manual escape hatch, wired to the "Clear local session" link on /login.
// Only called when the user explicitly opts in; never runs automatically.
export function nukeLocalSession() {
  try {
    Object.keys(window.localStorage).forEach((k) => {
      if (k.startsWith('sb-')) window.localStorage.removeItem(k);
    });
  } catch { /* ignore */ }
  try { window.sessionStorage.clear(); } catch { /* ignore */ }
}

if (!canPersist && typeof console !== 'undefined') {
  // eslint-disable-next-line no-console
  console.warn('[2120] localStorage is blocked; auth session will not persist across reloads.');
}
